import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { ProgressBarComponent, TypographyComponent } from '@purolator/ui-kit-angular';
import type { ProgressStep } from '@purolator/ui-kit-angular';
import { TranslationService, TranslatePipe } from '../../../../core/i18n';

/** Stepper step identifiers for the create-shipment flow. */
const STEPPER_STEP_KEYS = [
  'shipFromShipTo',
  'package',
  'service',
  'reviewAndPay',
  'getLabels',
] as const;

/** Wizard routes aligned with STEPPER_STEP_KEYS. */
const STEPPER_STEP_ROUTES = ['/', '/package', '/service', '/review', '/get-labels'] as const;

/**
 * Displays the Figma-aligned create-shipment stepper via kit progress bar (desktop labels + mobile segments).
 */
@Component({
  selector: 'app-shipment-stepper',
  imports: [TypographyComponent, ProgressBarComponent, TranslatePipe],
  templateUrl: './shipment-stepper.html',
  styleUrl: './shipment-stepper.scss',
})
export class ShipmentStepper {
  private readonly translation = inject(TranslationService);
  private readonly router = inject(Router);

  readonly activeStepIndex = input(0);
  readonly completeActiveStep = input(false);
  protected readonly totalSteps = STEPPER_STEP_KEYS.length;

  /** Kit only renders completed labels as buttons when stepClick is observed. */
  protected readonly stepNavigationEnabled = computed(() => !this.completeActiveStep());

  protected readonly mobileProgressCurrent = computed(() => String(this.activeStepIndex() + 1));
  protected readonly mobileProgressTotal = computed(() => String(this.totalSteps));

  /**
   * Mobile header shows the current step name (≤1199px layout).
   * Step 0 keeps the dedicated mobileSubtitle copy; later steps use stepper.steps labels.
   */
  protected readonly mobileStepLabel = computed(() => {
    this.translation.revision();
    const index = this.activeStepIndex();
    const key = STEPPER_STEP_KEYS[index] ?? STEPPER_STEP_KEYS[0];

    if (key === 'shipFromShipTo') {
      return this.translation.translate('createShipment.stepper.mobileSubtitle');
    }

    return this.translation.translate(`createShipment.stepper.steps.${key}`);
  });

  protected readonly progressSteps = computed((): ProgressStep[] => {
    this.translation.revision();
    const translate = (key: string) => this.translation.translate(key);
    return STEPPER_STEP_KEYS.map((key) => ({
      label: translate(`createShipment.stepper.steps.${key}`),
    }));
  });

  /**
   * Navigates to a completed prior wizard step from the desktop progress bar.
   * Skips the current step, Get Labels, and the completed-shipment view so existing
   * route restrictions (including Review leave confirmation) stay in effect.
   *
   * @param index Zero-based step index emitted by the kit progress bar
   */
  protected onStepClick(index: number): void {
    if (!this.stepNavigationEnabled() || index === this.activeStepIndex()) {
      return;
    }

    const url = STEPPER_STEP_ROUTES[index];
    if (!url || url === '/get-labels') {
      return;
    }

    this.router.navigateByUrl(url).catch(() => undefined);
  }
}
